import type { StatusType } from '@datadog/browser-logs';
import { TypedContainerModule } from '@thinkalpha/common/ioc/TypedContainer.js';
import type { DatadogService } from '@thinkalpha/ui-ioc/core/types/DatadogService';
import type { Logger as LoggerType, LogContext, LogData } from '@thinkalpha/ui-ioc/core/types/Logger';
import debugFactory from 'debug';
import { isEmpty } from 'es-toolkit/compat';
import type { StaticBindings } from 'src/types/bindings';
import { BaseLogger } from 'tslog';
import type { ILogObjMeta, ISettingsParam } from 'tslog';

const LogLevelId = {
    FATAL: 6,
    ERROR: 5,
    WARN: 4,
    INFO: 3,
    DEBUG: 2,
    TRACE: 1,
    SILLY: 0,
} as const;
type LogLevelId = (typeof LogLevelId)[keyof typeof LogLevelId];

/**
 * Datadog has many different status types, but we only send a subset of them based on our own log levels.
 */
type SupportedDatadogStatusType = Extract<StatusType, 'debug' | 'info' | 'warn' | 'error' | 'critical'>;

interface LogLevel {
    /**
     * Level name, used to map env vars onto a LogLevel
     */
    level: 'silly' | 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';
    tsLogId: LogLevelId;
    datadogId: SupportedDatadogStatusType | null;
}

const logLevels: LogLevel[] = [];
logLevels[LogLevelId.SILLY] = {
    level: 'silly',
    tsLogId: LogLevelId.SILLY,
    datadogId: null, // We don't support sending Silly logs to DD
};
logLevels[LogLevelId.TRACE] = {
    level: 'trace',
    tsLogId: LogLevelId.TRACE,
    datadogId: null, // We don't support sending Trace logs to DD
};
logLevels[LogLevelId.DEBUG] = {
    level: 'debug',
    tsLogId: LogLevelId.DEBUG,
    datadogId: 'debug',
};
logLevels[LogLevelId.INFO] = {
    level: 'info',
    tsLogId: LogLevelId.INFO,
    datadogId: 'info',
};
logLevels[LogLevelId.WARN] = {
    level: 'warn',
    tsLogId: LogLevelId.WARN,
    datadogId: 'warn',
};
logLevels[LogLevelId.ERROR] = {
    level: 'error',
    tsLogId: LogLevelId.ERROR,
    datadogId: 'error',
};
logLevels[LogLevelId.FATAL] = {
    level: 'fatal',
    tsLogId: LogLevelId.FATAL,
    datadogId: 'critical',
};

declare global {
    /**
     * The DEBUG filter used to filter local debug messages
     */
    const DEBUG: string | undefined;

    /**
     * The log level identifier which is used to determine what level of log to show in console
     */
    const LOG_LEVEL: LogLevel['level'];

    /**
     * Should we include trace logs?
     */
    const TRACE: boolean;

    /**
     * The namespace filter used only for local trace messages
     */
    const TRACE_FILTER: string | undefined;

    /**
     * The log level identifier which is used to determine what level of log to send to datadog
     */
    const LOG_LEVEL_DATADOG: LogLevel['level'];
}

/**
 * Determine if a given log level ID should make it to the Datadog transport
 */
const getDatadogLogId = (tsLogId: LogLevelId): LogLevel['datadogId'] | false => {
    const logLevel = logLevels.find((level) => level.tsLogId === tsLogId);
    if (!logLevel?.datadogId) {
        return false;
    }

    const datadogLevel: LogLevel =
        logLevels.find((level) => level.level === LOG_LEVEL_DATADOG) ?? logLevels[LogLevelId.DEBUG];

    if (logLevel.tsLogId >= datadogLevel.tsLogId) {
        return logLevel.datadogId;
    }

    return false;
};

export class Logger extends BaseLogger<LogData> implements LoggerType {
    silly(msg: string | LogData, context?: LogContext) {
        // When we pass a logger to a third-party library, it may choose to do silly logs we can't statically remove
        if (!TRACE) return;

        if (typeof msg === 'string') {
            return super.log(0, 'SILLY', { message: msg, ...(context || {}) });
        }
        return super.log(0, 'SILLY', msg);
    }
    trace(msg: string | LogData, context?: LogContext) {
        // When we pass a logger to a third-party library, it may choose to do trace logs we can't statically remove
        if (!TRACE) return;

        if (typeof msg === 'string') {
            return super.log(1, 'TRACE', { message: msg, ...(context || {}) });
        }
        return super.log(1, 'TRACE', msg);
    }
    debug(msg: string | LogData, context?: LogContext) {
        if (typeof msg === 'string') {
            return super.log(2, 'DEBUG', { message: msg, ...(context || {}) });
        }
        return super.log(2, 'DEBUG', msg);
    }
    info(msg: string | LogData, context?: LogContext) {
        if (typeof msg === 'string') {
            return super.log(3, 'INFO', { message: msg, ...(context || {}) });
        }
        return super.log(3, 'INFO', msg);
    }
    warn(msg: string | LogData, context?: LogContext) {
        if (typeof msg === 'string') {
            return super.log(4, 'WARN', { message: msg, ...(context || {}) });
        }
        return super.log(4, 'WARN', msg);
    }
    error(msg: string | LogData, context?: LogContext) {
        if (typeof msg === 'string') {
            return super.log(5, 'ERROR', { message: msg, ...(context || {}) });
        }
        return super.log(5, 'ERROR', msg);
    }
    fatal(msg: string | LogData, context?: LogContext) {
        if (typeof msg === 'string') {
            return super.log(6, 'FATAL', { message: msg, ...(context || {}) });
        }
        return super.log(6, 'FATAL', msg);
    }
    override getSubLogger(settings?: ISettingsParam<LogData>, logObj?: LogData): Logger {
        return super.getSubLogger(settings, logObj) as Logger;
    }
}

const getNamespace = (message: LogData & ILogObjMeta): string => {
    let name = message._meta.name;
    if (!name) {
        name = 'ta:misc';
    } else if (message._meta.parentNames) {
        name = `${message._meta.parentNames.join(':')}:${name}`;
    }

    return name;
};

const matchesNamespacePattern = (namespace: string, pattern: string): boolean => {
    if (!pattern.includes('*')) {
        return namespace === pattern;
    }

    let namespaceIndex = 0;
    let patternIndex = 0;
    let wildcardIndex = -1;
    let wildcardMatchIndex = 0;

    while (namespaceIndex < namespace.length) {
        if (patternIndex < pattern.length && pattern[patternIndex] === namespace[namespaceIndex]) {
            namespaceIndex += 1;
            patternIndex += 1;
        } else if (patternIndex < pattern.length && pattern[patternIndex] === '*') {
            wildcardIndex = patternIndex;
            wildcardMatchIndex = namespaceIndex;
            patternIndex += 1;
        } else if (wildcardIndex !== -1) {
            patternIndex = wildcardIndex + 1;
            wildcardMatchIndex += 1;
            namespaceIndex = wildcardMatchIndex;
        } else {
            return false;
        }
    }

    while (patternIndex < pattern.length && pattern[patternIndex] === '*') {
        patternIndex += 1;
    }

    return patternIndex === pattern.length;
};

export const createTraceNamespaceFilter = (filter: string | undefined): ((namespace: string) => boolean) => {
    const patterns = (filter ?? '').trim().replaceAll(/\s+/g, ',').split(',').filter(Boolean);
    const excluded = patterns.filter((pattern) => pattern.startsWith('-')).map((pattern) => pattern.slice(1));
    const included = patterns.filter((pattern) => !pattern.startsWith('-'));

    return (namespace: string) => {
        if (excluded.some((pattern) => matchesNamespacePattern(namespace, pattern))) {
            return false;
        }

        // An exclusion-only filter means "all trace namespaces except these".
        return included.length === 0 || included.some((pattern) => matchesNamespacePattern(namespace, pattern));
    };
};

function debugTransport() {
    const debuggers = new Map<string, ReturnType<typeof debugFactory>>();
    const isTraceNamespaceEnabled = createTraceNamespaceFilter(TRACE_FILTER);

    // Defaulting to showing all logs in console.
    // Note that `trace` logs will not show unless running the app with LOG_LEVEL=trace.
    debugFactory.enable(DEBUG ?? 'ta:*');

    const transport = (msg: LogData & ILogObjMeta) => {
        let namespace = getNamespace(msg);

        // Prepend component to namespace if present
        if (msg.component) {
            // namespace is already 'ta:...' so insert component after 'ta:'
            namespace = namespace.replace(/^ta:/, `ta:${msg.component}:`);
        }

        if (msg._meta.logLevelId === LogLevelId.TRACE && !isTraceNamespaceEnabled(namespace)) {
            return;
        }

        if (!debuggers.has(namespace)) {
            const debug = debugFactory(namespace);
            // We special-case these loggers since they print a large token to console, which is very noisy
            if (['ta:push:authentication', 'ta:table:authentication'].includes(namespace)) {
                debug.log = (...args) => {
                    console.groupCollapsed(namespace);
                    /* eslint-disable-next-line no-console -- direct console output */
                    console.trace(...args);
                    console.groupEnd();
                };
            }
            debuggers.set(namespace, debug);
        }

        // Strip component from logged context (already in namespace)
        const { message, _meta: _discarded, component: _component, ...context } = msg;

        const formatStr = `[${msg._meta.logLevelName}] ${message}`;

        if (isEmpty(context)) {
            debuggers.get(namespace)?.(formatStr);
        } else {
            debuggers.get(namespace)?.(`${formatStr} %o`, context);
        }
    };

    return transport;
}

/**
 * Preserve a caller-provided status without allowing it to override Datadog's reserved log-level field.
 */
export function rewriteReservedStatus(context: Record<string, unknown>): Record<string, unknown> {
    if (!Object.hasOwn(context, 'status')) {
        return context;
    }

    let rewrittenKey = '_status';
    while (Object.hasOwn(context, rewrittenKey)) {
        rewrittenKey = `_${rewrittenKey}`;
    }

    const { status, ...contextWithoutStatus } = context;
    return { ...contextWithoutStatus, [rewrittenKey]: status };
}

function datadogTransport(datadogLogger: DatadogService['logger']) {
    return (logObj: LogData & ILogObjMeta) => {
        const { message, _meta: _discarded, ...rawContext } = logObj;
        const context = rewriteReservedStatus(rawContext);

        const datadogId = getDatadogLogId(logObj._meta.logLevelId as LogLevelId);

        if (datadogId) {
            if (logObj.error && logObj.error instanceof Error) {
                // Pass Error instances as 4th param for proper stack trace indexing
                const { error: _, ...contextWithoutError } = context;
                datadogLogger.log(
                    message,
                    { namespace: getNamespace(logObj), ...contextWithoutError },
                    datadogId,
                    logObj.error,
                );
            } else {
                // Non-Error error values (if any) remain in context
                datadogLogger.log(message, { namespace: getNamespace(logObj), ...context }, datadogId);
            }
        }
    };
}

export const logsModule = new TypedContainerModule<StaticBindings>((bind) => {
    bind('Logger').toDynamicValue((ctx) => {
        const transports = [debugTransport()];

        if (ctx.container.isBound('DatadogService')) {
            const datadogLogger = ctx.container.get('DatadogService').logger;
            transports.push(datadogTransport(datadogLogger));
        }

        return new Logger({
            // Do not ship logs to console using the built-in pretty-printer or JSON format
            type: 'hidden',
            attachedTransports: transports,
            name: 'ta',
        });
    });
});
